import { availabilitySellTripSelection } from '@actions/availability.action';
import { flightValidateSelections } from '@actions/flight.action';
import { Injectable } from '@angular/core';
import {
  MODAL_DIALOG_CENTER,
  MODAL_DIALOG_CENTER_LG,
  MODAL_THREE_TAILS_IMG,
} from '@constants/app.constants';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  selectSearchInputCriteriaState,
  selectAvailabilitySelectedJourneysState,
  selectAvailabilitySelectedTripsState,
  selectAvailabilityTripTypeState,
} from '@selectors/availability.selector';
import { selectFlightsCopy } from '@selectors/cms.selector';
import { ModalService } from '@services/modal/modal.service';
import { validateFlightDates } from '@utils/flight.utils';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { appNoOp } from '../actions/app.action';
import { ConfirmationMessageModalComponent } from '@components/modals/confirmation-message-modal/confirmation-message-modal.component';
import { selectStationsState } from '@selectors/station.selector';
import { selectIsModifyPath } from '@selectors/router.selector';
import { TripType } from '@constants/home.constants';

@Injectable()
export class FlightEffect {
  validateSelections$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(flightValidateSelections),
      concatLatestFrom(() => [
        this.store.select(selectSearchInputCriteriaState),
        this.store.select(selectAvailabilitySelectedTripsState),
        this.store.select(selectAvailabilitySelectedJourneysState),
        this.store.select(selectFlightsCopy),
        this.store.select(selectStationsState),
        this.store.select(selectIsModifyPath),
        this.store.select(selectAvailabilityTripTypeState),
      ]),
      switchMap(([, criteria, keys, journeys, copy, stations, isModify, tripType]) => {
        if (
          criteria?.length !== keys?.length ||
          journeys?.some((j) => j === null)
        ) {
          import(
            '../../../components/modals/bottom-image-modal/bottom-image-modal.component'
          ).then(({ BottomImageModalComponent }) => {
            this.modalService.show(BottomImageModalComponent, {
              class: MODAL_DIALOG_CENTER_LG,
              initialState: {
                btnText: copy?.selectButton,
                text: copy?.pleaseSelectAllFlightsCopy,
                img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
                altText: 'Norse planes',
              },
            });
          });

          return of(appNoOp());
        } else if (journeys && !validateFlightDates(journeys)) {
          import(
            '../../../components/modals/bottom-image-modal/bottom-image-modal.component'
          ).then(({ BottomImageModalComponent }) => {
            this.modalService.show(BottomImageModalComponent, {
              class: MODAL_DIALOG_CENTER_LG,
              initialState: {
                btnText: copy?.selectButton,
                text: copy?.pleaseCheckYourDatesCopy,
                img: MODAL_THREE_TAILS_IMG,
                altText: 'norse plane',
              },
            });
          });

          return of(appNoOp());
        }
        if (
          tripType !== TripType.multiCity &&
          (journeys?.length ?? 0) > 1 &&
          (journeys?.[0]?.designator?.destination !==
            journeys?.[1]?.designator?.origin ||
            journeys?.[0]?.designator?.origin !==
              journeys?.[1]?.designator?.destination) &&
          !isModify
        ) {
          let modalText = ' ';
          if (
            journeys?.[0]?.designator.origin !==
            journeys?.[1]?.designator?.destination
          ) {
            modalText += copy?.originOneMismatch;
          }
          if (
            journeys?.[0]?.designator.destination !==
            journeys?.[1]?.designator?.origin
          ) {
            modalText += ' ' + copy?.originTwoMismatch;
          }
          const o1Station =
            stations?.find(
              (s) => s.stationCode === journeys?.[0]?.designator?.origin,
            )?.fullName +
            ' (' +
            journeys?.[0]?.designator?.origin +
            ')';
          const o2Station =
            stations?.find(
              (s) => s.stationCode === journeys?.[1]?.designator?.origin,
            )?.fullName +
            ' (' +
            journeys?.[1]?.designator?.origin +
            ')';
          const d1Station =
            stations?.find(
              (s) => s.stationCode === journeys?.[0]?.designator?.destination,
            )?.fullName +
            ' (' +
            journeys?.[0]?.designator?.destination +
            ')';
          const d2Station =
            stations?.find(
              (s) => s.stationCode === journeys?.[1]?.designator?.destination,
            )?.fullName +
            ' (' +
            journeys?.[1]?.designator?.destination +
            ')';

          const confirmModal = this.modalService.show(
            ConfirmationMessageModalComponent,
            {
              class: MODAL_DIALOG_CENTER,
              initialState: {
                confirmBtnText: '' + copy?.continueCopy,
                declineBtnText: '' + copy?.mismatchDecline,
                messageText: modalText
                  .replace('{origin1}', '' + o1Station)
                  .replace('{destination2}', '' + d2Station)
                  .replace('{origin2}', '' + o2Station)
                  .replace('{destination1}', '' + d1Station),
              },
            },
          );

          if (!confirmModal.content) {
            console.error('flight mis-match modal could not be displayed');
            return of(appNoOp());
          }

          return confirmModal.content?.response.pipe(
            take(1),
            switchMap((accepted) => {
              if (accepted) {
                return of(availabilitySellTripSelection());
              }
              return of(appNoOp());
            }),
          );
        } else {
          return of(availabilitySellTripSelection());
        }
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private modalService: ModalService,
  ) {}
}
